import { useState, useRef, useEffect } from 'react';
import { Smile, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReactionSummary } from '@/lib/api';

// Common emoji reactions - can be expanded
const QUICK_EMOJIS = ['👍', '👎', '❤️', '🎉', '😄', '😮', '👀', '🙏'];

const ALL_EMOJIS = [
  // Frequently used
  '👍', '👎', '❤️', '🎉', '😄', '😮', '👀', '🙏', '✅', '❌',
  // Emotions
  '😀', '😃', '😊', '🥰', '😍', '🤔', '😅', '😂', '🥳', '🤩',
  '😢', '😭', '😤', '😠', '🤯', '😱', '🙄', '😴', '🤮', '🤢',
  // Gestures
  '👏', '🙌', '💪', '🤝', '👋', '✌️', '🤞', '🫡', '🫶', '👊',
  // Objects & Symbols
  '🔥', '💯', '⭐', '💡', '🚀', '💎', '🏆', '🎯', '📌', '💬',
  '✨', '💥', '🌟', '⚡', '🔔', '📢', '🎨', '🛠️', '📝', '📎',
];

interface ReactionPickerProps {
  onSelect: (emoji: string) => void;
  className?: string;
}

export function ReactionPicker({ onSelect, className }: ReactionPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowAll(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (emoji: string) => {
    onSelect(emoji);
    setIsOpen(false);
    setShowAll(false);
  };

  const emojisToShow = showAll ? ALL_EMOJIS : QUICK_EMOJIS;

  return (
    <div className={cn('relative', className)} ref={pickerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-grey-600 transition-colors"
        title="Add reaction"
      >
        <Smile className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute z-[100] top-full mt-2 left-0 bg-white rounded-2xl shadow-xl border border-grey-200 p-3 min-w-[240px]">
          <div className={cn(
            'grid gap-1.5',
            showAll ? 'grid-cols-8' : 'grid-cols-5'
          )}>
            {emojisToShow.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleSelect(emoji)}
                className="w-10 h-10 flex items-center justify-center text-xl hover:bg-grey-100 rounded-xl transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
          
          {!showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="w-full mt-3 pt-3 border-t border-grey-100 text-xs text-grey-500 hover:text-secondary flex items-center justify-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              More emojis
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface ReactionDisplayProps {
  reactions: ReactionSummary[];
  onToggle: (emoji: string) => void;
  showPicker?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

export function ReactionDisplay({ 
  reactions, 
  onToggle, 
  showPicker = true,
  size = 'sm',
  className 
}: ReactionDisplayProps) {
  const [hoveredEmoji, setHoveredEmoji] = useState<string | null>(null);

  if (reactions.length === 0 && !showPicker) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {reactions.map((reaction) => (
        <div key={reaction.emoji} className="relative">
          <button
            onClick={() => onToggle(reaction.emoji)}
            onMouseEnter={() => setHoveredEmoji(reaction.emoji)}
            onMouseLeave={() => setHoveredEmoji(null)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border transition-colors',
              size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
              reaction.hasReacted
                ? 'bg-accent/10 border-accent/30 text-accent'
                : 'bg-grey-50 border-grey-200 text-grey-600 hover:border-grey-300'
            )}
          >
            <span>{reaction.emoji}</span>
            <span className="font-medium">{reaction.count}</span>
          </button>
          
          {/* Tooltip showing who reacted */}
          {hoveredEmoji === reaction.emoji && reaction.users.length > 0 && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-secondary text-white text-xs rounded-lg whitespace-nowrap z-50">
              {reaction.users.length <= 3 
                ? reaction.users.map(u => u.name).join(', ')
                : `${reaction.users.slice(0, 2).map(u => u.name).join(', ')} +${reaction.users.length - 2} more`
              }
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-secondary" />
            </div>
          )}
        </div>
      ))}
      
      {showPicker && (
        <ReactionPicker 
          onSelect={onToggle}
        />
      )}
    </div>
  );
}

interface ReactionsProps {
  entityType: 'message' | 'directMessage' | 'task' | 'taskComment';
  entityId: string;
  initialReactions?: ReactionSummary[];
  onReactionsChange?: (reactions: ReactionSummary[]) => void;
  showPicker?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Standalone Reactions component that handles its own API calls
 * Use this when you want reactions with automatic API handling
 */
export function Reactions({
  entityType,
  entityId,
  initialReactions = [],
  onReactionsChange,
  showPicker = true,
  size = 'sm',
  className,
}: ReactionsProps) {
  const [reactions, setReactions] = useState<ReactionSummary[]>(initialReactions);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchedId, setLastFetchedId] = useState<string | null>(null);

  // Fetch reactions on mount and when entityId changes
  useEffect(() => {
    // Skip if we already fetched for this entity
    if (lastFetchedId === entityId) return;
    
    const fetchReactions = async () => {
      try {
        const { reactionsApi } = await import('@/lib/api');
        const fetchedReactions = await reactionsApi.get(entityType, entityId);
        setReactions(fetchedReactions);
        setLastFetchedId(entityId);
      } catch (error) {
        console.error('Failed to fetch reactions:', error);
        setLastFetchedId(entityId);
      }
    };
    
    fetchReactions();
  }, [entityType, entityId, lastFetchedId]);

  // Update local state when initialReactions changes (if provided with data)
  useEffect(() => {
    if (initialReactions.length > 0) {
      setReactions(initialReactions);
    }
  }, [initialReactions]);

  const handleToggle = async (emoji: string) => {
    if (isLoading) return;
    
    setIsLoading(true);
    
    // Store current reactions for rollback
    const previousReactions = [...reactions];
    
    try {
      // Optimistic update
      const existingReaction = reactions.find(r => r.emoji === emoji);
      let newReactions: ReactionSummary[];
      
      if (existingReaction?.hasReacted) {
        // Remove reaction
        if (existingReaction.count === 1) {
          newReactions = reactions.filter(r => r.emoji !== emoji);
        } else {
          newReactions = reactions.map(r => 
            r.emoji === emoji 
              ? { ...r, count: r.count - 1, hasReacted: false }
              : r
          );
        }
      } else if (existingReaction) {
        // Add to existing reaction
        newReactions = reactions.map(r => 
          r.emoji === emoji 
            ? { ...r, count: r.count + 1, hasReacted: true }
            : r
        );
      } else {
        // New reaction
        newReactions = [...reactions, { emoji, count: 1, users: [], hasReacted: true }];
      }
      
      setReactions(newReactions);
      onReactionsChange?.(newReactions);

      // Import reactionsApi dynamically to avoid circular deps
      const { reactionsApi } = await import('@/lib/api');
      const result = await reactionsApi.toggle(entityType, entityId, emoji);
      
      // Update with server response
      setReactions(result.reactions);
      onReactionsChange?.(result.reactions);
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
      // Revert to previous state on error
      setReactions(previousReactions);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ReactionDisplay
      reactions={reactions}
      onToggle={handleToggle}
      showPicker={showPicker}
      size={size}
      className={className}
    />
  );
}

